import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Home, Briefcase, CalendarDays, Menu, Clock, CalendarClock, Bell, Sun, Moon } from 'lucide-react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useMobileTheme } from '../../context/MobileThemeContext';
import { consumePendingTap } from '../../services/PushService';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';

const MobileLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessSchedule, isWorker, isTimesheetOnly, isPPD } = useMobileAuth();
  const { theme, isDark, toggleTheme } = useMobileTheme();

  const timesheetOnly = isTimesheetOnly();
  const ppdOnly = isPPD();

  const [tapNotif, setTapNotif] = useState(null);
  useEffect(() => {
    // Chat pushes open the room directly; everything else shows the tap sheet
    const openTap = (detail) => {
      if (detail?.type === 'chat_message' && detail.roomId) {
        navigate(`/mobile/chat/${detail.roomId}`);
      } else {
        setTapNotif(detail || null);
      }
    };
    const handler = (e) => {
      if (e.detail?.type === 'chat_message') consumePendingTap();
      openTap(e.detail);
    };
    window.addEventListener('equipr-push-tap', handler);
    const pending = consumePendingTap();
    if (pending) openTap(pending);
    return () => window.removeEventListener('equipr-push-tap', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToJob = () => {
    const jobId = tapNotif?.jobId;
    setTapNotif(null);
    if (jobId) {
      navigate(isWorker() ? `/mobile/jobs/${jobId}` : `/mobile/schedule/${jobId}`);
    } else {
      navigate('/mobile/notifications');
    }
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/mobile/dashboard', testId: 'mobile-nav-home', visible: true },
    { icon: Clock, label: 'Timesheets', path: '/mobile/timesheet', testId: 'mobile-nav-timesheet', visible: timesheetOnly },
    { icon: CalendarClock, label: 'Leave', path: '/mobile/leave', testId: 'mobile-nav-leave', visible: timesheetOnly },
    { icon: Briefcase, label: 'My Jobs', path: '/mobile/jobs', testId: 'mobile-nav-jobs', visible: (isWorker() && !timesheetOnly) || ppdOnly },
    { icon: CalendarDays, label: 'Schedule', path: '/mobile/schedule', testId: 'mobile-nav-schedule', visible: canAccessSchedule() && !timesheetOnly && !ppdOnly },
    { icon: Menu, label: 'More', path: '/mobile/more', testId: 'mobile-nav-more', visible: !ppdOnly },
  ];

  const visibleNavItems = navItems.filter(item => item.visible);

  const isActive = (path) => {
    if (path === '/mobile/dashboard') return location.pathname === '/mobile/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'var(--m-bg)' }}>
      <div className="flex-1 min-h-0 overflow-y-auto pb-[calc(72px+env(safe-area-inset-bottom,0px))]">
        {children}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50" style={{ background: 'var(--m-bg-card)', borderTop: '1px solid var(--m-border)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-around py-2">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`relative flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
                  active ? 'text-[#ffd108]' : isDark ? 'text-[var(--m-text-secondary)] hover:text-[var(--m-text)]' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={item.testId}
              >
                <Icon className={`h-6 w-6 ${active ? 'text-[#ffd108]' : ''}`} />
                <span className={`text-xs mt-1 ${active ? 'font-medium' : ''}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Push notification tap sheet */}
      <Sheet open={!!tapNotif} onOpenChange={(o) => { if (!o) setTapNotif(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl" style={{ background: 'var(--m-bg)', borderColor: 'var(--m-border)' }} data-testid="push-tap-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-left" style={{ color: 'var(--m-text)' }}>
              <Bell className="h-5 w-5 text-[#ffd108]" /> {tapNotif?.title || 'Notification'}
            </SheetTitle>
          </SheetHeader>
          <div className="py-4">
            <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--m-text-body)' }}>{tapNotif?.body}</p>
          </div>
          <SheetFooter className="flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1"
              style={{ borderColor: 'var(--m-border-light)', color: 'var(--m-text)' }}
              onClick={() => { setTapNotif(null); navigate('/mobile/notifications'); }}
              data-testid="push-tap-all-btn"
            >
              All notifications
            </Button>
            <Button className="btn-volt flex-1" onClick={goToJob} data-testid="push-tap-job-btn">
              {tapNotif?.jobId ? 'View job' : 'Open'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileLayout;
